import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../lib/firebase";

type AppNotification={
  id:string;type:string;title:string;body:string;read:boolean;createdAt?:{toDate?:()=>Date};expiresAt?:{toDate?:()=>Date};
};

const iconFor=(type:string)=>type==="family-ping"?"✦":type==="home-hunt"?"⌂":type.startsWith("finance")?"$":"•";
const ageLabel=(value?:{toDate?:()=>Date})=>{
  const date=value?.toDate?.();if(!date)return"Just now";
  const minutes=Math.max(0,Math.floor((Date.now()-date.getTime())/60000));
  if(minutes<1)return"Just now";if(minutes<60)return`${minutes}m ago`;
  const hours=Math.floor(minutes/60);if(hours<24)return`${hours}h ago`;
  return date.toLocaleDateString(undefined,{month:"short",day:"numeric"});
};

export function NotificationBell({uid}:{uid:string}){
  const [open,setOpen]=useState(false),[items,setItems]=useState<AppNotification[]>([]);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    if(params.get("notifications")!=="1")return;
    setOpen(true);
    params.delete("notifications");
    const query=params.toString();
    window.history.replaceState(null,"",`${window.location.pathname}${query?`?${query}`:""}${window.location.hash}`);
  },[]);
  useEffect(()=>{
    const ref=query(collection(db,"users",uid,"notifications"),orderBy("createdAt","desc"),limit(80));
    return onSnapshot(ref,snapshot=>{
      const now=Date.now();
      setItems(snapshot.docs.map(document=>({id:document.id,...document.data()} as AppNotification)).filter(item=>!item.expiresAt?.toDate||item.expiresAt.toDate().getTime()>now));
    },()=>setItems([]));
  },[uid]);
  const unread=useMemo(()=>items.filter(item=>!item.read).length,[items]);
  async function markRead(item:AppNotification){
    if(!item.read)await updateDoc(doc(db,"users",uid,"notifications",item.id),{read:true,readAt:serverTimestamp()});
  }
  async function markAll(){
    const unreadItems=items.filter(item=>!item.read);if(!unreadItems.length)return;
    const batch=writeBatch(db);unreadItems.forEach(item=>batch.update(doc(db,"users",uid,"notifications",item.id),{read:true,readAt:serverTimestamp()}));await batch.commit();
  }
  return <>
    <button type="button" className="utility-button notification-bell" aria-label={unread?`Open notifications, ${unread} unread`:"Open notifications"} aria-haspopup="dialog" onClick={()=>setOpen(true)}>
      <span aria-hidden="true">🔔</span>{unread>0&&<b className="notification-badge">{unread>99?"99+":unread}</b>}
    </button>
    {open&&createPortal(<div className="notification-center-backdrop" role="presentation" onMouseDown={()=>setOpen(false)}>
      <section className="notification-center-dialog" role="dialog" aria-modal="true" aria-labelledby="notification-center-title" onMouseDown={event=>event.stopPropagation()}>
        <header><div><p className="eyebrow">Last seven days</p><h2 id="notification-center-title">Notifications</h2></div><button type="button" className="auth-close" aria-label="Close notifications" onClick={()=>setOpen(false)}>×</button></header>
        <div className="notification-center-actions"><span>{unread} unread</span><button type="button" disabled={!unread} onClick={()=>void markAll()}>Mark all read</button></div>
        <div className="notification-center-list">
          {!items.length&&<div className="notification-center-empty"><strong>You’re all caught up.</strong><small>Family and finance alerts will appear here.</small></div>}
          {items.map(item=><button type="button" className={item.read?"notification-center-item":"notification-center-item unread"} key={item.id} onClick={()=>void markRead(item)}>
            <i aria-hidden="true">{iconFor(item.type)}</i><span><strong>{item.title}</strong><small>{item.body}</small><em>{ageLabel(item.createdAt)}</em></span>{!item.read&&<b aria-label="Unread"/>}
          </button>)}
        </div>
      </section>
    </div>,document.body)}
  </>;
}
